import { defineStore } from 'pinia';

// Pinia replacement for the legacy Vuex `setting` module that came from the
// original Angle template. Holds layout-state booleans (sidebar collapsed,
// fixed layout, mobile aside toggled, etc.) consumed by SidebarToggle.js +
// Sidebar.vue. The body-class side effect that used to live in
// store/plugins/setting.js is now wired up via store.$subscribe in main.js.
export const useSettingStore = defineStore('setting', {
    state: () => ({
        /* Layout fixed. Scroll content only */
        isFixed: true,
        /* Sidebar collapsed */
        isCollapsed: true,
        /* Boxed layout */
        isBoxed: false,
        /* Floating sidebar */
        isFloat: false,
        /* Sidebar show menu on hover only */
        asideHover: false,
        /* Show sidebar scrollbar (don't hide it) */
        asideScrollbar: false,
        /* Sidebar collapsed with big icons and text */
        isCollapsedText: false,
        /* Toggle for the offsidebar */
        offsidebarOpen: false,
        /* Toggle for the sidebar offcanvas (mobile) */
        asideToggled: false,
        /* Toggle for the sidebar user block */
        showUserBlock: false,
        /* Enables layout horizontal */
        horizontal: false,
        /* Full size layout */
        useFullLayout: false,
        /* Hide footer */
        hiddenFooter: false
    }),
    getters: {
        // Preserves the old `getSetting` accessor — a snapshot of all flags.
        getSetting: state => state
    },
    actions: {
        toggleSetting(name) {
            if (name in this) {
                this[name] = !this[name];
            }
        },
        changeSetting({ name, value }) {
            if (name in this) {
                this[name] = value;
            }
        }
    }
});

// Body-class side effect — used to live in store/plugins/setting.js as a Vuex
// plugin. Call once at app boot (after Pinia is mounted) to apply initial
// classes and subscribe for future changes.
const updateElementClass = (el, stat, name) =>
    el && el.classList[stat ? 'add' : 'remove'](name);
const updateBodyClass = (stat, name) => updateElementClass(document.body, stat, name);

const applyClasses = state => {
    updateBodyClass(state.isFixed, 'layout-fixed');
    updateBodyClass(state.isBoxed, 'layout-boxed');
    updateBodyClass(state.isCollapsed, 'aside-collapsed');
    updateBodyClass(state.isCollapsedText, 'aside-collapsed-text');
    updateBodyClass(state.isFloat, 'aside-float');
    updateBodyClass(state.asideHover, 'aside-hover');
    updateBodyClass(state.offsidebarOpen, 'offsidebar-open');
    updateBodyClass(state.asideToggled, 'aside-toggled');
    updateBodyClass(state.horizontal, 'layout-h');
    updateElementClass(document.querySelector('.sidebar'), state.asideScrollbar, 'show-scrollbar');
};

export function installSettingBodyClasses() {
    const store = useSettingStore();
    const apply = () => applyClasses(store.$state);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', apply);
    } else {
        apply();
    }
    store.$subscribe(apply);
}
